import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { subscriberService } from '../../services/firebase/subscriberService';
import './Unsubscribe.css';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const subscriberId = searchParams.get('id');

  const [status, setStatus] = useState('loading'); // loading, confirming, success, already_unsubscribed, error
  const [subscriber, setSubscriber] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const checkSubscriber = async () => {
      if (!subscriberId) {
        setStatus('error');
        return;
      }

      try {
        const sub = await subscriberService.getById(subscriberId);
        if (!sub) {
          setStatus('error');
          return;
        }

        setSubscriber(sub);

        if (sub.status === 'unsubscribed') {
          setStatus('already_unsubscribed');
        } else {
          setStatus('confirming');
        }
      } catch (error) {
        console.error('Error checking subscriber:', error);
        setStatus('error');
      }
    };

    checkSubscriber();
  }, [subscriberId]);

  const handleUnsubscribe = async () => {
    setIsProcessing(true);

    try {
      await subscriberService.unsubscribe(subscriberId);
      setStatus('success');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="unsubscribe-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        );

      case 'confirming':
        return (
          <div className="unsubscribe-confirm">
            <div className="unsubscribe-icon unsubscribe-icon--warning">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
                <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5"/>
              </svg>
            </div>
            <h2>Unsubscribe from Newsletter</h2>
            <p className="unsubscribe-email">
              {subscriber?.email}
            </p>
            <p className="unsubscribe-message">
              Are you sure you want to unsubscribe? You will no longer receive our wellness tips,
              event announcements, and exclusive offers.
            </p>
            <div className="unsubscribe-actions">
              <Button
                onClick={handleUnsubscribe}
                disabled={isProcessing}
                variant="secondary"
              >
                {isProcessing ? 'Unsubscribing...' : 'Yes, Unsubscribe Me'}
              </Button>
              <Link to="/" className="unsubscribe-cancel">
                No, Keep Me Subscribed
              </Link>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="unsubscribe-success">
            <div className="unsubscribe-icon unsubscribe-icon--success">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2>You've Been Unsubscribed</h2>
            <p className="unsubscribe-message">
              We're sorry to see you go. You have been successfully removed from our mailing list
              and will no longer receive newsletter emails from KO Holistic Health.
            </p>
            <p className="unsubscribe-note">
              Changed your mind? You can always resubscribe through our website.
            </p>
            <div className="unsubscribe-actions">
              <Link to="/">
                <Button>Return to Homepage</Button>
              </Link>
            </div>
          </div>
        );

      case 'already_unsubscribed':
        return (
          <div className="unsubscribe-already">
            <div className="unsubscribe-icon unsubscribe-icon--info">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </div>
            <h2>Already Unsubscribed</h2>
            <p className="unsubscribe-message">
              This email address has already been unsubscribed from our mailing list.
              You won't receive any more newsletter emails from us.
            </p>
            <div className="unsubscribe-actions">
              <Link to="/">
                <Button>Return to Homepage</Button>
              </Link>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="unsubscribe-error">
            <div className="unsubscribe-icon unsubscribe-icon--error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h2>Something Went Wrong</h2>
            <p className="unsubscribe-message">
              We couldn't process your unsubscribe request. The link may be invalid or expired.
            </p>
            <p className="unsubscribe-note">
              If you're having trouble, please contact us directly and we'll help you manage your email preferences.
            </p>
            <div className="unsubscribe-actions">
              <Link to="/contact">
                <Button>Contact Us</Button>
              </Link>
              <Link to="/" className="unsubscribe-cancel">
                Return to Homepage
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <SEO
        title="Unsubscribe | KO Holistic Health"
        description="Manage your email subscription preferences for KO Holistic Health newsletters."
        url="/unsubscribe"
        noindex={true}
      />

      <div className="unsubscribe-page">
        <section className="unsubscribe-hero">
          <div className="container">
            <h1>Email Preferences</h1>
          </div>
        </section>

        <section className="section unsubscribe-content">
          <div className="container">
            <Card className="unsubscribe-card">
              {renderContent()}
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default Unsubscribe;
